import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { startExternalAuthentication, startSignupEmail, type ExternalAuthProvider } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/AuthShell";
import { canUseRequiredCookies } from "./required-cookie";

const EMAIL_PATTERN = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/iu;

export function SignupPage() {
  const { t } = useTranslation("auth");
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) return <Navigate to="/console/dashboard" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized) || normalized.length > 254) {
      setError("有効なメールアドレスを入力してください。");
      return;
    }
    if (!canUseRequiredCookies()) {
      setError(t("error.requiredCookieUnavailable"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await startSignupEmail(normalized);
      setAccepted(true);
    } catch {
      setError("現在、確認メールを受け付けられません。時間をおいて再度お試しください。");
    } finally {
      setBusy(false);
    }
  };

  const startExternal = async (provider: ExternalAuthProvider) => {
    if (!canUseRequiredCookies()) {
      setError(t("error.requiredCookieUnavailable"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await startExternalAuthentication(provider, { intent: "signup" });
    } catch {
      setError("外部ログインを開始できませんでした。時間をおいて再度お試しください。");
      setBusy(false);
    }
  };

  return (
    <AuthShell title="アカウントを作成" description="登録に使うログイン方法を選択してください。">
      {accepted ? (
        <div className="space-y-4">
          <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            入力された宛先へ確認手順を送信しました。メール内のリンクを30分以内に開いてください。
          </div>
          <p className="text-sm text-muted-foreground">登録状況の有無にかかわらず、画面には同じ案内を表示します。</p>
          <Button type="button" variant="outline" onClick={() => setAccepted(false)}>別のメールアドレスでやり直す</Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={submit} noValidate>
          {error ? <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div> : null}
          <div className="space-y-2">
            <Label htmlFor="signup-email">メールアドレス</Label>
            <Input id="signup-email" type="email" autoComplete="email" value={email}
              onChange={(event) => setEmail(event.target.value)} disabled={busy} />
          </div>
          <Button className="w-full" type="submit" disabled={busy}>{busy ? "送信中…" : "確認メールを送信"}</Button>
          <div className="text-center text-xs text-muted-foreground">または</div>
          <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={() => void startExternal("google")}>Googleで登録</Button>
          <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={() => void startExternal("microsoft")}>Microsoftで登録</Button>
          <p className="text-xs text-muted-foreground">Googleは確認済みメールをアカウントメールとして提案します。Microsoftは認証後にアカウントメールの確認が必要です。外部アカウントのメールは、後で変更するアカウントメールと一致し続ける必要はありません。</p>
        </form>
      )}
      <div className="mt-5 text-center text-sm"><Link className="text-primary underline" to="/login">ログインへ戻る</Link></div>
    </AuthShell>
  );
}
